import React from 'react';

/**
 * Ein WA-Block des Zeitstrahls (Header mit Balken, optional aufgeklappt mit Aktivitaeten-Liste).
 * Reines Refactor-Extract aus AnalysisPanel.jsx; Verhalten unveraendert.
 *
 * @param {{
 *   waKey: string|number,
 *   list: Array<object>,
 *   expanded: boolean,
 *   onToggle: (waKey: string|number) => void,
 *   getColor: (m: object) => string,
 *   formatTime: (n: number) => string,
 *   onSegmentEnter: (e: React.MouseEvent, m: object) => void,
 *   onSegmentLeave: () => void,
 *   compact?: boolean,
 * }} props
 */
function ZeitstrahlWaBlock({
  waKey,
  list,
  expanded,
  onToggle,
  getColor,
  formatTime,
  onSegmentEnter,
  onSegmentLeave,
  compact = false,
}) {
  const totalIstWa = list.reduce((s, m) => s + (m.individualTime || 0), 0);
  const label = waKey === '—' ? 'Ohne WA-Nr' : `WA-Nr ${waKey}`;
  const tätigkeitenText = list.map(m => m.kuerzel || m.activity || ('#' + (m.number ?? ''))).filter(Boolean).join(' · ') || '—';

  return (
    <div className={`compact-zeitstrahl-wa-block ${compact ? 'compact-zeitstrahl-wa-block--small' : ''}`}>
      <div
        className="compact-zeitstrahl-wa-header"
        onClick={() => onToggle(waKey)}
        title={expanded ? 'Zuklappen' : 'Aufklappen'}
      >
        <div className="compact-zeitstrahl-header-track-bg" aria-hidden="true">
          <div className="compact-zeitstrahl-track">
            {list.map((m, idx) => {
              const pct = totalIstWa > 0 ? ((m.individualTime || 0) / totalIstWa) * 100 : 0;
              const color = getColor(m);
              return (
                <div
                  key={`${waKey}-${m.number}-${idx}`}
                  className="compact-zeitstrahl-segment"
                  style={{ width: pct + '%', backgroundColor: color, minWidth: pct > 0 ? 2 : 0 }}
                  onMouseEnter={(e) => onSegmentEnter(e, m)}
                  onMouseLeave={onSegmentLeave}
                />
              );
            })}
          </div>
        </div>
        <div className="compact-zeitstrahl-wa-header-fg">
          <span className="compact-zeitstrahl-chevron">{expanded ? '▼' : '▶'}</span>
          <div className="compact-zeitstrahl-label" title={`${label} · ${formatTime(totalIstWa)}`}>
            {label} · {formatTime(totalIstWa)}
          </div>
          <div className="compact-zeitstrahl-header-spacer" />
        </div>
      </div>
      {!expanded && (
        <div className="compact-zeitstrahl-sublabel compact-zeitstrahl-sublabel-indent" title={tätigkeitenText}>
          {tätigkeitenText}
        </div>
      )}
      {expanded && (
        <div className="compact-zeitstrahl-drilldown">
          {list.map((m, idx) => {
            const pctStart = totalIstWa > 0
              ? (list.slice(0, idx).reduce((s, x) => s + (x.individualTime || 0), 0) / totalIstWa) * 100
              : 0;
            const pct = totalIstWa > 0 ? ((m.individualTime || 0) / totalIstWa) * 100 : 0;
            const color = getColor(m);
            const rowLabel = [m.kuerzel, m.activity, '#' + (m.number ?? '')].find(Boolean) || '—';
            return (
              <div
                key={`${waKey}-${m.number}-${idx}`}
                className="compact-zeitstrahl-activity-row"
              >
                <div className="compact-zeitstrahl-activity-bar-bg" aria-hidden="true">
                  <div className="compact-zeitstrahl-track compact-zeitstrahl-track-positioned">
                    <div
                      className="compact-zeitstrahl-segment"
                      style={{
                        position: 'absolute',
                        left: pctStart + '%',
                        width: pct + '%',
                        height: '100%',
                        backgroundColor: color,
                        borderRadius: 2
                      }}
                      onMouseEnter={(e) => onSegmentEnter(e, m)}
                      onMouseLeave={onSegmentLeave}
                    />
                  </div>
                </div>
                <div className="compact-zeitstrahl-activity-row-fg">
                  <span className="compact-zeitstrahl-activity-spacer" />
                  <div
                    className="compact-zeitstrahl-activity-label"
                    onMouseEnter={(e) => onSegmentEnter(e, m)}
                    onMouseLeave={onSegmentLeave}
                  >
                    #{m.number} {rowLabel} · {formatTime(m.individualTime || 0)}
                  </div>
                  <div className="compact-zeitstrahl-activity-row-spacer" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default React.memo(ZeitstrahlWaBlock);
